import React, { useMemo, useState, useEffect, useCallback } from "react";
import { GlobalStyles } from "@mui/material";
import { Drawer, IconButton, Divider, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import dayjs, { Dayjs } from "dayjs";
import CreatedTimeFilter from "../../admin-section/features/provider-management/provider-requests/provider-requests-filter-options/created-timer-filter";
import "./filter.scss";

export type FilterValues = {
  fromDate?: string;
  toDate?: string;
};

type FilterProps = {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
  initialValues?: Partial<FilterValues>;
  onReset?: () => void;
  serviceCategoryOptions?: string[];
};

const DEFAULT_VALUES: FilterValues = {
  fromDate: "",
  toDate: "",
};

export default function Filter({
  open,
  onClose,
  onApply,
  initialValues,
  onReset,
}: FilterProps) {
  const [fromDate, setCreatedFrom] = useState<Dayjs | null>(null);
  const [toDate, setCreatedTo] = useState<Dayjs | null>(null);

  const formikValidate = useCallback((vals: FilterValues) => {
    const errors: Partial<Record<keyof FilterValues, string>> = {};
    if (vals.fromDate || vals.toDate) {
      if (!vals.fromDate) {
        errors.fromDate = "Start date is required";
      }

      if (!vals.toDate) {
        errors.toDate = "End date is required";
      }

      if (vals.fromDate && vals.toDate) {
        const startDate = dayjs(vals.fromDate);
        const endDate = dayjs(vals.toDate);

        if (!startDate.isValid()) {
          errors.fromDate = "Invalid start date";
        }

        if (!endDate.isValid()) {
          errors.toDate = "Invalid end date";
        }

        if (startDate.isAfter(endDate)) {
          errors.fromDate = "Start date cannot be after end date";
          errors.toDate = "End date cannot be before start date";
        }
      }
    }

    return errors;
  }, []);

  const formik = useFormik<FilterValues>({
    initialValues: DEFAULT_VALUES,
    validate: formikValidate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      const payload: FilterValues = {
        ...vals,
      };
      onApply(payload);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    const nextValues: FilterValues = {
      ...DEFAULT_VALUES,
      ...initialValues,
      fromDate: initialValues?.fromDate ?? "",
      toDate: initialValues?.toDate ?? "",
    };
    formik.resetForm({ values: nextValues });
    setCreatedFrom(nextValues.fromDate ? dayjs(nextValues.fromDate) : null);

    setCreatedTo(nextValues.toDate ? dayjs(nextValues.toDate) : null);
  }, [open, initialValues]);

  const handleReset = () => {
    formik.resetForm({ values: DEFAULT_VALUES });
    setCreatedFrom(null);
    setCreatedTo(null);
    onReset?.();
  };

  const handleCreatedDateRange = useCallback(
    (from: Date | null, to: Date | null) => {
      const today = dayjs();

      const fromDayjs = from ? dayjs(from) : null;
      const toDayjs = to ? dayjs(to) : null;
      setCreatedFrom(fromDayjs);
      setCreatedTo(toDayjs);

      const isFromValid = fromDayjs && fromDayjs.isValid();
      const isToValid = toDayjs && toDayjs.isValid();

      if (fromDayjs && !fromDayjs.isValid()) {
        formik.setFieldError("fromDate", "Invalid date");
      } else if (fromDayjs && fromDayjs.isAfter(today)) {
        formik.setFieldError("fromDate", "Future date not allowed");
      } else {
        formik.setFieldError("fromDate", undefined);
      }

      if (toDayjs && !toDayjs.isValid()) {
        formik.setFieldError("toDate", "Invalid date");
      } else if (toDayjs && toDayjs.isAfter(today)) {
        formik.setFieldError("toDate", "Future date not allowed");
      } else if (fromDayjs && toDayjs && toDayjs.isBefore(fromDayjs)) {
        formik.setFieldError("toDate", "To date cannot be before From date");
      } else {
        formik.setFieldError("toDate", undefined);
      }
      formik.setFieldValue(
        "fromDate",
        isFromValid ? fromDayjs.toISOString() : "",
        false,
      );

      formik.setFieldValue(
        "toDate",
        isToValid ? toDayjs.toISOString() : "",
        false,
      );

      formik.setFieldTouched("fromDate", true, false);
      formik.setFieldTouched("toDate", true, false);
    },
    [formik],
  );

  const hasAnyFilterApplied = useMemo(() => {
    const v = formik.values;

    return Boolean(v.fromDate) || Boolean(v.toDate);
  }, [formik.values]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 2300 }}
      PaperProps={{
        className: "provider-management-filter__paper",
        sx: {
          width: 300,
          maxWidth: "90vw",
        },
      }}
    >
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-popper .MuiPaper-root": {
            padding: "0 !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-listbox": {
            padding: "0 !important",
            margin: "0 !important",
            maxHeight: "unset !important",
            height: "auto !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-noOptions": {
            padding: "6px 12px !important",
            minHeight: "unset !important",
            lineHeight: "1.2 !important",
          },
        }}
      />
      <div className="provider-management-filter">
        <div className="filterDrawer__header">
          <Typography
            variant="h6"
            className="filterDrawer__title"
            sx={{ fontWeight: 600 }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={onClose}
            className="filterDrawer__closeBtn"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        <div className="filterDrawer__body">
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Date Range</Typography>

            <CreatedTimeFilter
              value="Custom"
              onChange={() => {}}
              isCreatedTimeEnabled={true}
              onDateRangeChange={handleCreatedDateRange}
              createdTimeError={
                formik.errors.fromDate || formik.errors.toDate || ""
              }
              createdTimeStartError={formik.errors.fromDate || ""}
              createdTimeEndError={formik.errors.toDate || ""}
              setCreatedTimeFromDate={setCreatedFrom}
              setCretedTimeToDate={setCreatedTo}
              showDropdown={false}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        </div>
        {hasAnyFilterApplied && (
          <>
            <Divider />
            <div className="filterDrawer__footer">
              <Button
                variant="outlined"
                onClick={handleReset}
                className="filterDrawer__btn filterDrawer__btn--cancel"
                sx={{
                  backgroundColor: "transparent",
                  color: "black",
                  border: "1px solid #9e9e9e",
                  boxShadow: "none",
                  "&:focus": {
                    outline: "none",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#EBD28D", color: "black" }}
                onClick={() => formik.handleSubmit()}
                className="filterDrawer__btn filterDrawer__btn--apply"
                disabled={!formik.isValid}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
